import React, { ReactNode, useContext } from 'react';
import styled, { css } from 'styled-components';
import { MinHeightProps, SpaceProps } from 'styled-system';
import { breakpoints, colors, gutterWidth } from 'theme';

import { FilestackImageData } from 'api/shared';
import Box from 'components/Box';
import { Image } from 'components/Image';
import Parallax, { PARALLAX_DEFAULT_SPEED } from 'containers/Parallax';
import AmpContext from 'utils/AmpContext';
import { imageUrl } from 'utils/filestack';
import { getImageSrc } from 'utils/getImageSrc';
import { retinaBackgroundImage } from 'utils/style';

const filestackTransform = (width: number, height: number) =>
  `resize=w:${width},h:${height}/quality=value:70`;

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const BannerImage = styled(Image)<{ $mobileImageHandle?: any }>`
  ${(props) =>
    props.$mobileImageHandle
      ? `
        display: none;
        @media (min-width: ${breakpoints[3]}) {
          display: block;
        }
      `
      : `
        display: block;
      `}
`;
const MobileBannerImage = styled(Image)`
  display: block;
  @media (min-width: ${breakpoints[3]}) {
    display: none;
  }
`;
const Banner = styled(
  ({
    requestIsAmp,
    image,
    mobileImage,
    alwaysShow,
    convert,
    parallax,
    ...props
  }) => {
    const isSvg = image && image.mimetype.includes('image/svg');
    const isSvgMobile =
      mobileImage && mobileImage.mimetype.includes('image/svg');
    return (
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react/jsx-props-no-spreading
      <Box {...props}>
        {mobileImage && mobileImage.handle && (
          <Box display={['block', null, null, 'none']} height="100%">
            <MobileBannerImage
              width={992}
              height={605}
              alt={getText('Hero image')}
              src={getImageSrc({
                handle: mobileImage.handle,
                width: 992,
                height: 605,
                format: convert ? 'jpg' : undefined,
                mimetype: mobileImage.mimetype,
              })}
              srcSet={`${getImageSrc({
                handle: mobileImage.handle,
                width: 992,
                height: 605,
                format: convert ? 'jpg' : undefined,
                mimetype: mobileImage.mimetype,
              })},
                ${getImageSrc({
                  handle: mobileImage.handle,
                  width: 1984,
                  height: 1210,
                  format: convert ? 'jpg' : undefined,
                  mimetype: mobileImage.mimetype,
                })} 2x`}
              alwaysShow={alwaysShow}
              svg={isSvgMobile}
            />
          </Box>
        )}
        <BannerImage
          data-qa-id="hero-image-image"
          $mobileImageHandle={mobileImage && mobileImage.handle}
          width={1920}
          height={1024}
          alt={getText('Hero image')}
          src={getImageSrc({
            handle: image.handle,
            width: 1920,
            height: 1024,
            format: convert ? 'jpg' : undefined,
            mimetype: image.mimetype,
          })}
          srcSet={`${getImageSrc({
            handle: image.handle,
            width: 1920,
            height: 1024,
            format: convert ? 'jpg' : undefined,
            mimetype: image.mimetype,
          })},
            ${getImageSrc({
              handle: image.handle,
              width: 2400,
              height: parallax ? 1600 : 1152,
              format: convert ? 'jpg' : undefined,
              mimetype: image.mimetype,
            })} 2x`}
          alwaysShow={alwaysShow}
          svg={isSvg}
        />
      </Box>
    );
  }
)`
  background-color: ${colors.battleshipGrey};
  background-size: cover;
  background-position: center;
  ${(props) =>
    props.mobileImage || props.image
      ? retinaBackgroundImage(
          imageUrl({
            handle: props.mobileImage
              ? props.mobileImage.handle
              : props.image && props.image.handle,
            mimetype: props.mobileImage
              ? props.mobileImage.mimetype
              : props.image && props.image.mimetype,
            transform: filestackTransform(992, 605),
            format: props.convert ? 'jpg' : undefined,
          }),
          {
            filename2x: imageUrl({
              handle: props.mobileImage
                ? props.mobileImage.handle
                : props.image && props.image.handle,
              mimetype: props.mobileImage
                ? props.mobileImage.mimetype
                : props.image && props.image.mimetype,
              transform: filestackTransform(1984, 1210),
              format: props.convert ? 'jpg' : undefined,
            }),
            include3x: false,
          }
        )
      : css`
          background-image: none;
        `}
  @media (min-width: ${breakpoints[2]}) {
    ${(props) =>
      props.image
        ? retinaBackgroundImage(
            imageUrl({
              handle: props.image.handle,
              mimetype: props.image.mimetype,
              transform: filestackTransform(1920, 1024),
              format: props.convert ? 'jpg' : undefined,
            }),
            {
              filename2x: imageUrl({
                handle: props.image.handle,
                mimetype: props.image.mimetype,
                transform: filestackTransform(
                  2400,
                  props.parallax ? 1600 : 1152
                ),
                format: props.convert ? 'jpg' : undefined,
              }),
              include3x: false,
            }
          )
        : css`
            background-image: none;
          `}
  }
  img {
    display: none;
    height: 100%;
    width: 100%;
  }
  ${(props) =>
    props.requestIsAmp
      ? ''
      : `
  @supports (object-fit: cover) {
    background-image: none;
    img {
      display: block;
      object-fit: cover;
    }
  }`}
`;

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition
const Hero = ({
  heroImage,
  mobileHeroImage,
  children,
  withOverlay,
  withLightOverlay,
  parallax,
  alwaysShow,
  ...props
}: SpaceProps &
  MinHeightProps & {
    heroImage: FilestackImageData | null | undefined;
    mobileHeroImage?: FilestackImageData | null | undefined;
    children: ReactNode;
    withOverlay?: boolean;
    withLightOverlay?: boolean;
    'data-qa-id': string | null | undefined;
    parallax?: boolean;
    alwaysShow?: boolean;
  }) => {
  const requestIsAmp = useContext(AmpContext);
  return (
    <Box
      position="relative"
      display="flex"
      width={1}
      minHeight={['320px', null, null, '450px']}
      px={[gutterWidth / 2, null, null, gutterWidth]}
      py={[48, null, null, 96]}
      justifyContent="center"
      alignItems="center"
      color="white"
      data-qa-id={props['data-qa-id']}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    >
      <Box
        position="absolute"
        top={0}
        left={0}
        width={1}
        height="100%"
        overflow="hidden"
      >
        <Parallax
          y={[0, null]}
          width={1}
          height="100%"
          disabled={!parallax || requestIsAmp}
        >
          {(heroImage || mobileHeroImage) && (
            <Banner
              position="absolute"
              transform="translateZ(0)"
              bottom={0}
              left={0}
              width={1}
              height={
                parallax ? `calc(100% + ${PARALLAX_DEFAULT_SPEED}px)` : '100%'
              }
              image={heroImage}
              convert={!(heroImage && heroImage.mimetype === 'image/jpeg')}
              mobileImage={mobileHeroImage}
              data-qa-id="hero-image"
              requestIsAmp={requestIsAmp}
              alwaysShow={alwaysShow || parallax}
              parallax={parallax}
            />
          )}
        </Parallax>
        {(withOverlay || withLightOverlay) && (
          <Box
            position="absolute"
            transform="translateZ(0)"
            top={0}
            left={0}
            width={1}
            height="100%"
            bg={`rgba(0,0,0,${withLightOverlay ? 0.2 : 0.4})`}
          />
        )}
      </Box>
      <Box
        position="relative"
        transform="rotateZ(0)"
        display="flex"
        justifyContent="center"
        alignItems="center"
        flexDirection="column"
        width="100%"
      >
        {children}
      </Box>
    </Box>
  );
};

export default Hero;
